import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Users,
  IndianRupee,
  Layers,
  Search,
  Unlock,
  Download,
  CheckCircle2,
  FileText,
  Mail,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import {
  retryAttendeeZohoInvoice,
  resendAttendeeConfirmationEmail,
} from '../../lib/api';
import { useAdminContext } from './adminContext';
import { AdminOfflineBooking } from '../../components/AdminOfflineBooking';

export function AdminSalesPage() {
  const queryClient = useQueryClient();
  const {
    adminKey,
    currentUser,
    metrics,
    booths,
    attendees = [],
    refetchMetrics,
    refetchAttendees,
    refetchSystemHealth,
    handleExportCsv,
    isExporting,
    forceReleaseMutation,
    setActionSuccessMessage,
  } = useAdminContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'visitors' ? 'visitors' : 'booths';
  const [salesSubTab, setSalesSubTab] = useState<'booths' | 'visitors'>(initialTab);

  const handleTabChange = (tab: 'booths' | 'visitors') => {
    setSalesSubTab(tab);
    setSearchParams(tab === 'visitors' ? { tab: 'visitors' } : {});
  };

  // Booths table search & filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'on_hold' | 'booked'>('all');

  // Visitors / Delegates table search & filter state
  const [delegateSearch, setDelegateSearch] = useState('');
  const [delegateTierFilter, setDelegateTierFilter] = useState<string>('all');

  const isReadOnlyOps = (currentUser?.role as string) === 'operations';

  // Retry Zoho GST Invoice for Attendee
  const retryAttendeeInvoiceMutation = useMutation({
    mutationFn: (attendeeId: string) => retryAttendeeZohoInvoice(adminKey, attendeeId),
    onSuccess: (res: any) => {
      setActionSuccessMessage(res?.message || 'Zoho GST Invoice generated successfully for delegate.');
      refetchMetrics();
      if (refetchAttendees) refetchAttendees();
      if (refetchSystemHealth) refetchSystemHealth();
      queryClient.invalidateQueries({ queryKey: ['adminParticipants'] });
      setTimeout(() => setActionSuccessMessage(null), 4000);
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to generate Zoho GST invoice for delegate.');
    },
  });

  // Resend Confirmation / Fix Email for Attendee
  const resendAttendeeEmailMutation = useMutation({
    mutationFn: ({ id, email }: { id: string; email?: string }) =>
      resendAttendeeConfirmationEmail(adminKey, id, email ? { email } : {}),
    onSuccess: (res: any) => {
      setActionSuccessMessage(
        res?.message || 'Delegate confirmation email & digital pass dispatched successfully.'
      );
      refetchMetrics();
      if (refetchAttendees) refetchAttendees();
      queryClient.invalidateQueries({ queryKey: ['adminParticipants'] });
      setTimeout(() => setActionSuccessMessage(null), 4000);
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to resend delegate confirmation email.');
    },
  });

  // Filter booths
  const filteredBooths = booths.filter((b) => {
    const matchesSearch =
      b.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.companyName && b.companyName.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Filter delegates / attendees
  const filteredAttendees = attendees.filter((a: any) => {
    const q = delegateSearch.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (a.fullName && a.fullName.toLowerCase().includes(q)) ||
      (a.email && a.email.toLowerCase().includes(q)) ||
      (a.phone && a.phone.toLowerCase().includes(q)) ||
      (a.companyName && a.companyName.toLowerCase().includes(q)) ||
      (a.badgeCode && a.badgeCode.toLowerCase().includes(q));
    const matchesTier = delegateTierFilter === 'all' || a.tier === delegateTierFilter;
    return matchesSearch && matchesTier;
  });

  const totalBooths = metrics?.boothsBreakdown?.total || booths.length || 1;
  const bookedOrHold =
    (metrics?.boothsBreakdown?.booked || 0) + (metrics?.boothsBreakdown?.on_hold || 0);
  const occupancyPercent = ((bookedOrHold / totalBooths) * 100).toFixed(1);

  const formattedRevenue = (metrics?.revenueTotalInr || 0).toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  });

  const totalAttendees = metrics?.attendeesBreakdown?.totalRegistered || attendees.length || 0;
  const checkedInAttendees = metrics?.attendeesBreakdown?.totalCheckedIn || 0;
  const checkInRate =
    totalAttendees > 0 ? ((checkedInAttendees / totalAttendees) * 100).toFixed(1) : '0.0';

  const tierLabels: Record<string, string> = {
    vip: 'VIP Delegate',
    conference_delegate: 'Conference Delegate',
    trade_visitor: 'Trade Visitor',
    exhibitor: 'Exhibitor Rep',
    exhibitor_pass: 'Exhibitor Pass',
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Sub-tab Navigation */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-2 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleTabChange('booths')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              salesSubTab === 'booths'
                ? 'bg-[#0A3D27] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Building2 className="w-4 h-4" />
            <span>Booths &amp; Stalls Management</span>
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('visitors')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              salesSubTab === 'visitors'
                ? 'bg-[#0A3D27] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Visitors &amp; Delegates Management ({attendees.length || totalAttendees})</span>
          </button>
        </div>

        {isReadOnlyOps && (
          <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-bold">
            Operations View-Only Mode
          </span>
        )}
      </div>

      {/* SUB-TAB A: BOOTHS */}
      {salesSubTab === 'booths' && (
        <div className="space-y-6">
          {/* Booth Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 relative overflow-hidden group hover:border-slate-300 transition-all shadow-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Total Stall Revenue
                </span>
                <div className="w-8 h-8 rounded-xl bg-[#0A3D27]/10 text-[#0A3D27] flex items-center justify-center">
                  <IndianRupee className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-slate-900 tracking-tight">₹{formattedRevenue}</div>
              <p className="text-xs text-slate-500 mt-1">
                Confirmed from {metrics?.boothsBreakdown?.booked || 0} booked stalls
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0A3D27] to-[#C5A059]" />
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 relative overflow-hidden group hover:border-slate-300 transition-all shadow-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Pavilion 3 Occupancy
                </span>
                <div className="w-8 h-8 rounded-xl bg-[#FAF5EA] text-[#785613] flex items-center justify-center border border-[#E6C687]">
                  <Building2 className="w-4 h-4" />
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-slate-900 tracking-tight">{occupancyPercent}%</span>
                <span className="text-xs text-slate-500">
                  ({bookedOrHold}/{totalBooths} stalls)
                </span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2 overflow-hidden">
                <div
                  className="bg-[#0A3D27] h-full rounded-full transition-all duration-500"
                  style={{ width: `${occupancyPercent}%` }}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0A3D27] to-emerald-500" />
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 relative overflow-hidden group hover:border-slate-300 transition-all shadow-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Stall Inventory Status
                </span>
                <div className="w-8 h-8 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                  <Layers className="w-4 h-4" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mt-1">
                <div className="bg-[#F8F9F6] p-2 rounded-xl border border-slate-200">
                  <div className="text-xs text-[#0A3D27] font-bold">
                    {metrics?.boothsBreakdown?.available ?? 0}
                  </div>
                  <div className="text-[10px] text-slate-500 uppercase">Available</div>
                </div>
                <div className="bg-[#F8F9F6] p-2 rounded-xl border border-slate-200">
                  <div className="text-xs text-amber-700 font-bold">
                    {metrics?.boothsBreakdown?.on_hold ?? 0}
                  </div>
                  <div className="text-[10px] text-slate-500 uppercase">On Hold</div>
                </div>
                <div className="bg-[#F8F9F6] p-2 rounded-xl border border-slate-200">
                  <div className="text-xs text-rose-700 font-bold">
                    {metrics?.boothsBreakdown?.booked ?? 0}
                  </div>
                  <div className="text-[10px] text-slate-500 uppercase">Booked</div>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C5A059] to-amber-600" />
            </div>
          </div>

          {/* Stall Directory Table */}
          <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-4 bg-slate-50/50">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Stall Reservation Directory</h3>
                <p className="text-xs text-slate-500">
                  Search stalls, monitor holds, and force-release expired locks
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="Search Stall ID or Company..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-[#F8F9F6] border border-slate-300 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>

                <div className="flex items-center bg-slate-100 border border-slate-200 rounded-xl p-1 text-xs">
                  {(['all', 'available', 'on_hold', 'booked'] as const).map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setStatusFilter(filter)}
                      className={`px-2.5 py-1 rounded-lg font-medium transition-colors capitalize cursor-pointer ${
                        statusFilter === filter
                          ? 'bg-white text-slate-900 font-bold shadow-xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {filter.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4 font-semibold">Stall Number</th>
                    <th className="py-3 px-4 font-semibold">Dimensions</th>
                    <th className="py-3 px-4 font-semibold">Area</th>
                    <th className="py-3 px-4 font-semibold">Price (INR)</th>
                    <th className="py-3 px-4 font-semibold">Status</th>
                    <th className="py-3 px-4 font-semibold">Reserved Company / Lock</th>
                    <th className="py-3 px-4 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredBooths.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-500">
                        No exhibition stalls matching the selected filters.
                      </td>
                    </tr>
                  ) : (
                    filteredBooths.map((booth) => {
                      return (
                        <tr key={booth.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-4 font-bold text-slate-900">{booth.id}</td>
                          <td className="py-3 px-4 text-slate-600">{booth.dimensions}</td>
                          <td className="py-3 px-4 text-slate-600">{booth.areaSqm} sqm</td>
                          <td className="py-3 px-4 text-slate-800 font-medium">
                            ₹{Number(booth.priceInr).toLocaleString('en-IN')}
                          </td>
                          <td className="py-3 px-4">
                            {booth.status === 'booked' ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                                Booked
                              </span>
                            ) : booth.status === 'on_hold' ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                On Hold
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-[#0A3D27] border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                                Available
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-600">
                            {booth.companyName ? (
                              <span className="font-semibold text-slate-800">{booth.companyName}</span>
                            ) : booth.lockedUntil ? (
                              <span className="text-[11px] text-amber-700">
                                Locked until{' '}
                                {new Date(booth.lockedUntil).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {!isReadOnlyOps && (booth.status === 'on_hold' || booth.lockedUntil) ? (
                              <button
                                onClick={() => {
                                  if (confirm(`Force release stall ${booth.id} back to available?`)) {
                                    forceReleaseMutation.mutate(booth.id);
                                  }
                                }}
                                disabled={forceReleaseMutation.isPending}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-lg text-xs font-semibold transition-all cursor-pointer"
                              >
                                <Unlock className="w-3 h-3" />
                                <span>Force Release</span>
                              </button>
                            ) : (
                              <span className="text-slate-400 text-xs">Standard</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Offline Manual Stall Allocation & Invoicing Section */}
          {!isReadOnlyOps && (
            <div className="border-t border-slate-200 pt-6">
              <AdminOfflineBooking
                adminKey={adminKey}
                defaultForm="stall"
                activeSection="booths"
                hideSwitchTabs={true}
              />
            </div>
          )}
        </div>
      )}

      {/* SUB-TAB B: VISITORS & DELEGATES */}
      {salesSubTab === 'visitors' && (
        <div className="space-y-6">
          {/* Visitor Overview Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 relative overflow-hidden group hover:border-slate-300 transition-all shadow-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Registered Delegates
                </span>
                <div className="w-8 h-8 rounded-xl bg-[#0A3D27]/10 text-[#0A3D27] flex items-center justify-center">
                  <Users className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-slate-900 tracking-tight">{totalAttendees}</div>
              <p className="text-xs text-slate-500 mt-1">Across VIP, Delegates, Visitors &amp; Exhibitors</p>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0A3D27] to-[#C5A059]" />
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 relative overflow-hidden group hover:border-slate-300 transition-all shadow-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Admitted Ingress
                </span>
                <div className="w-8 h-8 rounded-xl bg-[#FAF5EA] text-[#785613] flex items-center justify-center border border-[#E6C687]">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-slate-900 tracking-tight">{checkedInAttendees}</span>
                <span className="text-xs text-slate-500">({checkInRate}% admitted)</span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2 overflow-hidden">
                <div
                  className="bg-[#C5A059] h-full rounded-full transition-all duration-500"
                  style={{ width: `${checkInRate}%` }}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C5A059] to-amber-600" />
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col justify-between shadow-xs">
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                  Data Operations
                </span>
                <p className="text-xs text-slate-600">
                  Download complete delegate roster including contact details, GSTIN, and B2B survey profiles.
                </p>
              </div>
              <button
                onClick={handleExportCsv}
                disabled={isExporting}
                className="w-full mt-3 py-2.5 px-4 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-xs transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer"
              >
                <Download className={`w-4 h-4 ${isExporting ? 'animate-bounce' : ''}`} />
                <span>{isExporting ? 'Exporting Roster...' : 'Export Attendee Roster (CSV)'}</span>
              </button>
            </div>
          </div>

          {/* Delegate Tier Breakdown Grid */}
          {metrics?.attendeesBreakdown?.byTier && (
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-xs">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                Registration &amp; Check-in by Delegate Tier
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Object.entries(metrics.attendeesBreakdown.byTier).map(([tierKey, data]) => {
                  return (
                    <div key={tierKey} className="bg-[#F8F9F6] p-3 rounded-xl border border-slate-200">
                      <span className="text-[11px] font-semibold text-slate-600 block truncate">
                        {tierLabels[tierKey] || tierKey}
                      </span>
                      <div className="flex items-baseline justify-between mt-1">
                        <span className="text-lg font-bold text-slate-900">{data.registered}</span>
                        <span className="text-xs text-[#0A3D27] font-semibold">
                          {data.checkedIn} scanned
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Unified Delegates & Passes Directory Table + 1-Click Recovery UI (Decision D10) */}
          <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-4 bg-slate-50/50">
              <div>
                <h3 className="text-sm font-bold text-slate-900">
                  Delegates &amp; Passes Directory ({filteredAttendees.length})
                </h3>
                <p className="text-xs text-slate-500">
                  Manage registered delegates, verify Zoho GST invoices, and trigger 1-click invoice or email recovery
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="Search Name, Email, Company, Phone..."
                    value={delegateSearch}
                    onChange={(e) => setDelegateSearch(e.target.value)}
                    className="bg-[#F8F9F6] border border-slate-300 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] min-w-[240px]"
                  />
                </div>

                <select
                  value={delegateTierFilter}
                  onChange={(e) => setDelegateTierFilter(e.target.value)}
                  className="bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-700 cursor-pointer focus:outline-none focus:border-[#0A3D27]"
                >
                  <option value="all">All Pass Tiers</option>
                  <option value="vip">VIP Delegate</option>
                  <option value="conference_delegate">Conference Delegate</option>
                  <option value="trade_visitor">Trade Visitor</option>
                  <option value="exhibitor">Exhibitor Rep</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 uppercase text-[10px] tracking-wider font-bold">
                  <tr>
                    <th className="py-3 px-4">Delegate Name &amp; Contact</th>
                    <th className="py-3 px-4">Company</th>
                    <th className="py-3 px-4">Pass Tier</th>
                    <th className="py-3 px-4">Payment Status</th>
                    <th className="py-3 px-4">Zoho GST Invoice</th>
                    <th className="py-3 px-4 text-right">1-Click Recovery Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAttendees.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-500">
                        No delegates or visitor passes match the current search filter.
                      </td>
                    </tr>
                  ) : (
                    filteredAttendees.map((attendee: any) => {
                      const paymentStatus = attendee.paymentStatus || 'paid';
                      const amountPaidPaise = Number(attendee.amountPaidPaise ?? attendee.amountPaise ?? 0);
                      const isPaidOrder =
                        paymentStatus === 'paid' &&
                        (amountPaidPaise > 0 ||
                          attendee.tier === 'conference_delegate' ||
                          attendee.tier === 'vip');
                      const invoiceNumber = attendee.zohoInvoiceNumber || attendee.zohoInvoiceId;
                      const invoiceUrl = attendee.zohoInvoiceUrl;
                      const isMissingInvoice = isPaidOrder && !invoiceNumber && attendee.paymentMethod !== 'offline' && attendee.paymentMethod !== 'exhibitor_comp';

                      return (
                        <tr key={attendee.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-4">
                            <div className="font-bold text-slate-900">{attendee.fullName}</div>
                            <div className="text-[11px] text-slate-500">{attendee.email}</div>
                            {attendee.phone && (
                              <div className="text-[10px] text-slate-400 font-mono">{attendee.phone}</div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-semibold text-slate-800">
                              {attendee.companyName || '—'}
                            </div>
                            {attendee.badgeCode && (
                              <span className="text-[10px] font-mono text-slate-500">
                                Badge: {attendee.badgeCode}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[#FAF5EA] text-[#785613] border border-[#E6C687]">
                              {tierLabels[attendee.tier] || attendee.tier || 'Delegate'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {paymentStatus === 'paid' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-[#0A3D27] border border-emerald-200">
                                <CheckCircle2 className="w-3 h-3" />
                                <span>Paid</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 capitalize">
                                {paymentStatus}
                              </span>
                            )}
                            {amountPaidPaise > 0 && (
                              <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                                ₹{(amountPaidPaise / 100).toLocaleString('en-IN')}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {invoiceNumber ? (
                              invoiceUrl ? (
                                <a
                                  href={invoiceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-[#0A3D27] border border-emerald-200 font-mono text-[11px] font-bold hover:underline"
                                >
                                  <FileText className="w-3 h-3" />
                                  <span>{invoiceNumber}</span>
                                  <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 border border-slate-200 font-mono text-[11px] font-bold">
                                  <FileText className="w-3 h-3 text-[#0A3D27]" />
                                  <span>{invoiceNumber}</span>
                                </span>
                              )
                            ) : isMissingInvoice ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-bold">
                                <AlertTriangle className="w-3 h-3" />
                                <span>⚠️ Missing Invoice</span>
                              </span>
                            ) : (
                              <span className="text-slate-400 text-[11px]">Complimentary / Free</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {!isReadOnlyOps && (
                              <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => retryAttendeeInvoiceMutation.mutate(attendee.id)}
                                  disabled={retryAttendeeInvoiceMutation.isPending}
                                  title="Generate or Retry Zoho Books GST Invoice"
                                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-[#0A3D27] border border-emerald-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  <FileText className="w-3 h-3" />
                                  <span>Retry Zoho Invoice</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    const updatedEmail = window.prompt(
                                      'Confirm or enter corrected email address to resend confirmation & QR badge:',
                                      attendee.email || ''
                                    );
                                    if (updatedEmail !== null && updatedEmail.trim()) {
                                      resendAttendeeEmailMutation.mutate({
                                        id: attendee.id,
                                        email: updatedEmail.trim(),
                                      });
                                    }
                                  }}
                                  disabled={resendAttendeeEmailMutation.isPending}
                                  title="Resend Confirmation Email or Fix Typo in Email Address"
                                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  <Mail className="w-3 h-3" />
                                  <span>Resend Confirmation / Fix Email</span>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Offline Manual Attendee Pass Issuance */}
          {!isReadOnlyOps && (
            <div className="border-t border-slate-200 pt-6">
              <AdminOfflineBooking
                adminKey={adminKey}
                defaultForm="attendee"
                activeSection="visitors"
                hideSwitchTabs={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminSalesPage;
